/**
 * Guest basket — the set of item IDs a not-signed-in visitor has added
 * to their basket, persisted in localStorage so it survives a page
 * refresh, a tab close, and the round-trip through the auth flow.
 *
 * On sign-in / sign-up the items are POSTed to /api/basket/merge in
 * one round-trip and the guest store is cleared. Everywhere else in the
 * app reads / writes through these helpers, never localStorage directly,
 * so the storage key + shape stay in one place.
 */
import { apiFetch } from './api';

const KEY = 'lr-guest-basket';

export function getGuestBasket(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Dedupe + filter out anything that isn't a string.
    return [...new Set(parsed.filter((v): v is string => typeof v === 'string'))];
  } catch {
    return [];
  }
}

function setGuestBasket(items: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    // Quota / private-mode — best-effort only.
  }
  // Notify the layout's bag count to refresh.
  window.dispatchEvent(new CustomEvent('lr:basket-changed'));
}

export function addToGuestBasket(itemId: string): void {
  const current = getGuestBasket();
  if (current.includes(itemId)) return;
  setGuestBasket([...current, itemId]);
}

export function removeFromGuestBasket(itemId: string): void {
  const current = getGuestBasket();
  setGuestBasket(current.filter((id) => id !== itemId));
}

export function clearGuestBasket(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent('lr:basket-changed'));
}

/**
 * Called once on sign-in / sign-up success. POSTs the guest item-ids to
 * the server, then clears the local store. Silently ignores failures so
 * we don't block the auth flow on a network blip — the customer can re-add
 * if the merge silently fails.
 */
export async function mergeGuestBasketIntoServer(): Promise<number> {
  const items = getGuestBasket();
  if (items.length === 0) return 0;
  try {
    const res = await apiFetch<{ added: number }>('/api/basket/merge', {
      method: 'POST',
      body: JSON.stringify({ item_ids: items })
    });
    clearGuestBasket();
    return res.added ?? 0;
  } catch {
    // Leave the guest items in place so a retry on the next page load
    // gets another shot.
    return 0;
  }
}
